/**
 * Tax calculation utilities for displaying prices with taxes included
 */

export interface TaxCalculation {
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRate: number;
}

/**
 * Calculate price with tax included from base price and tax rate
 */
export const calculatePriceWithTax = (basePrice: number, taxRate: number): TaxCalculation => {
  const subtotal = basePrice;
  const taxAmount = basePrice * (taxRate / 100);
  const total = subtotal + taxAmount;
  
  return {
    subtotal,
    taxAmount,
    total,
    taxRate
  };
};

/**
 * Calculate price with tax included from price breakdown
 */
export const calculateFromPriceBreakdown = (priceBreakdown: {
  subtotal: number;
  tax_amount: number;
  total: number;
  tax_rate: number;
}): TaxCalculation => {
  return {
    subtotal: priceBreakdown.subtotal,
    taxAmount: priceBreakdown.tax_amount,
    total: priceBreakdown.total,
    taxRate: priceBreakdown.tax_rate
  };
};

/**
 * Calculate total price with tax for multiple items
 */
export const calculateTotalWithTax = (
  items: Array<{
    quantity: number;
    price_at_time: number;
    tax_rate_at_time: number;
    price_breakdown?: {
      subtotal: number;
      tax_amount: number;
      total: number;
      tax_rate: number;
    };
  }>
): TaxCalculation => {
  let totalSubtotal = 0;
  let totalTaxAmount = 0;
  let totalAmount = 0;
  let averageTaxRate = 0;

  items.forEach(item => {
    if (item.price_breakdown) {
      // Use price breakdown if available
      const itemTotal = item.price_breakdown.total * item.quantity;
      const itemSubtotal = item.price_breakdown.subtotal * item.quantity;
      const itemTaxAmount = item.price_breakdown.tax_amount * item.quantity;
      
      totalSubtotal += itemSubtotal;
      totalTaxAmount += itemTaxAmount;
      totalAmount += itemTotal;
    } else {
      // Calculate from individual item prices
      const itemCalculation = calculatePriceWithTax(item.price_at_time, item.tax_rate_at_time);
      const itemTotal = itemCalculation.total * item.quantity;
      const itemSubtotal = itemCalculation.subtotal * item.quantity;
      const itemTaxAmount = itemCalculation.taxAmount * item.quantity;
      
      totalSubtotal += itemSubtotal;
      totalTaxAmount += itemTaxAmount;
      totalAmount += itemTotal;
    }
  });

  // Calculate average tax rate
  if (totalSubtotal > 0) {
    averageTaxRate = (totalTaxAmount / totalSubtotal) * 100;
  }

  return {
    subtotal: totalSubtotal,
    taxAmount: totalTaxAmount,
    total: totalAmount,
    taxRate: averageTaxRate
  };
};

/**
 * Format price with tax information for display
 */
export const formatPriceWithTax = (
  basePrice: number,
  taxRate: number,
  formatPrice: (amount: number) => string
): string => {
  const calculation = calculatePriceWithTax(basePrice, taxRate);
  return formatPrice(calculation.total);
};

/**
 * Format price with tax breakdown for detailed display
 */
export const formatPriceWithTaxBreakdown = (
  basePrice: number,
  taxRate: number,
  formatPrice: (amount: number) => string
): {
  subtotal: string;
  taxAmount: string;
  total: string;
  taxRate: number;
} => {
  const calculation = calculatePriceWithTax(basePrice, taxRate);
  
  return {
    subtotal: formatPrice(calculation.subtotal),
    taxAmount: formatPrice(calculation.taxAmount),
    total: formatPrice(calculation.total),
    taxRate: calculation.taxRate
  };
};
